import { Component } from '@theme/component';

/**
 * Fit guide model viewer: the size tabs and previous / next arrows scroll a
 * snapping track of model slides, and the current slide is reflected back on
 * the tabs and arrows as the shopper swipes. The track stays swipeable without
 * this module.
 *
 * @typedef {object} FitGuideRefs
 * @property {HTMLElement} track - The scroll-snapping row of slides.
 * @property {HTMLButtonElement[]} [tabs] - One button per slide, in slide order.
 * @property {HTMLButtonElement} [previous] - Shows the previous slide.
 * @property {HTMLButtonElement} [next] - Shows the next slide.
 *
 * @extends {Component<FitGuideRefs>}
 */
class BrsFitGuide extends Component {
  requiredRefs = ['track'];

  /** @type {AbortController | undefined} */
  #listenerController;

  /** @type {number | undefined} */
  #frame;

  connectedCallback() {
    super.connectedCallback();
    this.#listen();
    this.#sync();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#listen();
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#listenerController?.abort();
    this.#listenerController = undefined;
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = undefined;
  }

  /** @returns {number} Index of the slide currently in view. */
  get index() {
    const { track } = this.refs;
    if (!track.clientWidth) return 0;
    return Math.round(Math.abs(track.scrollLeft) / track.clientWidth);
  }

  /** @returns {number} */
  get count() {
    return this.refs.track.children.length;
  }

  /**
   * Scrolls to the slide at the given index.
   * @param {number} index
   */
  show(index) {
    const { track } = this.refs;
    const target = Math.max(0, Math.min(index, this.count - 1));
    const rtl = getComputedStyle(track).direction === 'rtl';
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    track.scrollTo({
      left: track.clientWidth * target * (rtl ? -1 : 1),
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
    this.#sync(target);
  }

  /** @param {Event} event - Click on a size tab carrying data-index. */
  selectSize(event) {
    const tab = event.target;
    if (!(tab instanceof HTMLElement)) return;
    this.show(Number(tab.dataset.index) || 0);
  }

  previous() {
    this.show(this.index - 1);
  }

  next() {
    this.show(this.index + 1);
  }

  #listen() {
    this.#listenerController?.abort();
    this.#listenerController = new AbortController();
    this.refs.track.addEventListener('scroll', this.#scheduleSync, {
      passive: true,
      signal: this.#listenerController.signal,
    });
  }

  #scheduleSync = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = undefined;
      this.#sync();
    });
  };

  /** @param {number} [current] - Slide to mark current; defaults to the one in view. */
  #sync(current = this.index) {
    const { tabs = [], previous, next } = this.refs;

    for (const [i, tab] of tabs.entries()) {
      if (i === current) tab.setAttribute('aria-current', 'true');
      else tab.removeAttribute('aria-current');
    }

    if (previous) previous.disabled = current <= 0;
    if (next) next.disabled = current >= this.count - 1;
  }
}

if (!customElements.get('brs-fit-guide')) {
  customElements.define('brs-fit-guide', BrsFitGuide);
}
